/**
 * « Générer une activité » du tuteur hors live (`tutor/students/GenerateActivityModal.js`) : ce que le
 * parcours partagé (`useGenerateActivityFlow`) ne sait pas lire du tuteur — ses élèves d'une activité
 * commune, le mode programme de révision, le quiz de prérequis hors salle, et la source d'une activité
 * dont on génère de semblables (`ParcoursChoiceModal.js`).
 *
 * Formes du backend lâches, toutes optionnelles : une ligne inutilisable est écartée, jamais jetée.
 */
import { hasFeature } from "../../../../../data/utility/Utils"
import { type UIActivityGenerationField } from "../../../ui/types"
import { type UITutorStudent } from "../../../ui/types/tutorEleves"
import { type ApiSchoolSubject } from "../../parent/shared/schoolSubjectAdapter"
import { tutorStudentIdentity } from "./tutorStudentAdapter"

type Id = string | number

/** Un élève du tuteur tel que le compte le porte (`currentUser.object.students`). */
export type ApiGenerateStudent = {
    id?: Id | null
    display_name?: string | null
    firstname?: string | null
    lastname?: string | null
    avatar?: string | null
    has_study_program?: boolean | null
    profile?: { class?: string | null; interests?: string | null } | null
    todo_activities?: (ApiGenerateActivity | null)[] | null
    activities?: (ApiGenerateActivity | null)[] | null
    history?: { activities?: (ApiGenerateActivity | null)[] | null } | null
}

/** Une activité de l'élève : son support, dont on tire des semblables. */
export type ApiGenerateActivity = {
    id?: Id | null
    material?: {
        id?: Id | null
        title?: string | null
        level?: string | number | null
        school_subject_id?: Id | null
        class?: string | null
        structured_data?: { analysis?: { confidence?: string | null } | null } | null
    } | null
}

/**
 * Les élèves du choix d'une activité commune (`GenerateActivityModal`, « Sélectionner les élèves » :
 * l'avatar et `display_name`, dans l'ordre de « Mes élèves »).
 */
export function toGenerateTargetStudents(
    students: ReadonlyArray<ApiGenerateStudent | null | undefined> | null | undefined
): UITutorStudent[] {
    return (students ?? [])
        .map((student) => tutorStudentIdentity(student))
        .filter((identity): identity is NonNullable<typeof identity> => identity != null)
        .map((identity) => ({ ...identity, subjects: [] }))
}

/**
 * Le mode programme est offert quand au moins un élève visé a un programme (`GenerateActivityModal.js`,
 * l'effet « Follow the selected students' study-program availability »).
 */
export function anyHasStudyProgram(
    students: ReadonlyArray<ApiGenerateStudent | null | undefined> | null | undefined,
    ids: readonly Id[]
): boolean {
    const wanted = new Set(ids.map(String))
    return (students ?? []).some(
        (student) => student?.id != null && wanted.has(String(student.id)) && !!student.has_study_program
    )
}

/**
 * Le quiz de prérequis hors live : la salle de la PREMIÈRE séance du tuteur décide (`liveRoomToCheck`,
 * « fall back to first live session » du legacy), son label `prerequisite-quiz`.
 */
export function prerequisiteQuizAvailable(
    account: { live_sessions?: ({ live_room?: unknown } | null)[] | null } | null
): boolean {
    return hasFeature(account?.live_sessions?.[0]?.live_room, "prerequisite-quiz") === true
}

/**
 * Le quiz allumé d'office sur les cartes d'« Entraînement » (1) et de « Révision du cours » (2)
 * (`MaterialResultsCards.js`, « Default quiz toggle ON »), éteint pour « Approfondissement ».
 */
export function quizOnByDefault(level: string | null | undefined): boolean {
    return level === "1" || level === "2"
}

// ── Le mode programme de révision ────────────────────────────────────────────

/** Un programme lu par `getStudyProgramsAsTutor`, ce dont le cadrage a besoin. */
export type ApiGenerateProgram = {
    id?: Id | null
    class?: string | null
    school_subject?: ApiSchoolSubject | null
    study_program_items?:
        | ({
              chapter?: { id?: Id | null; class?: string | null; school_subject_id?: Id | null } | null
          } | null)[]
        | null
}

/**
 * Le cadrage que porte un chapitre du programme : en mode programme, la classe et la matière en
 * viennent, jamais demandées (`watchedStudyProgramChapter?.class`, `.school_subject_id`). Le chapitre
 * sert sa classe (`ChapterResource`) ; à défaut, celle du programme.
 */
export function programChapterScope(
    programs: ReadonlyArray<ApiGenerateProgram | null | undefined> | null | undefined,
    chapterId: Id
): { className: string | null; subjectId: string | null } | null {
    for (const program of programs ?? []) {
        for (const item of program?.study_program_items ?? []) {
            const chapter = item?.chapter
            if (chapter?.id == null || String(chapter.id) !== String(chapterId)) continue
            const className = chapter.class ?? program?.class ?? null
            const subjectId = chapter.school_subject_id ?? program?.school_subject?.id ?? null
            return { className: className || null, subjectId: subjectId != null ? String(subjectId) : null }
        }
    }
    return null
}

// ── « Générer à partir de l'exo » (la fiche d'une activité) ─────────────────

/** Le support d'une activité, tel que `TodoModal` le passe à `ParcoursChoiceModal`. */
export type TutorGenerateSource = {
    materialId: Id
    title: string | null
    /** `sourceLevel` : le type de l'exo, pré-rempli. */
    level: string | null
    /** `material.structured_data.analysis.confidence` : sous `high`, son chapitre se choisit d'abord. */
    confidence: string | null
    /** `material.school_subject_id` : la matière dont on liste les chapitres. */
    subjectId: Id | null
    /** `material.class` : la classe du support, quand celle de l'élève manque. */
    className: string | null
}

/** Une activité de l'élève, telle que le store ou la fiche d'une activité la tient. */
export type ApiGenerateSourceActivity = NonNullable<NonNullable<ApiGenerateStudent["todo_activities"]>[number]>

function sourceOfActivity(activity: ApiGenerateSourceActivity | null | undefined): TutorGenerateSource | null {
    const material = activity?.material
    if (material?.id == null) return null
    return {
        materialId: material.id,
        title: material.title ?? null,
        level: material.level != null ? String(material.level) : null,
        confidence: material.structured_data?.analysis?.confidence ?? null,
        subjectId: material.school_subject_id ?? null,
        className: material.class ?? null,
    }
}

/**
 * Le support de l'activité : celle que la fiche tient (`held`, lue par `getActivities` — une terminée
 * dépliée de la liste paginée n'est pas au store), sinon ce que le store porte de l'élève : à faire
 * (`todo_activities`), toutes (`activities`, `getStudent`), puis son historique. Absente, ou sans
 * support : `null`.
 */
export function generationSourceOf(
    student: ApiGenerateStudent | null | undefined,
    activityId: Id,
    held?: ApiGenerateSourceActivity | null
): TutorGenerateSource | null {
    if (held?.id != null && String(held.id) === String(activityId)) {
        const source = sourceOfActivity(held)
        if (source != null) return source
    }
    const lists = [student?.todo_activities, student?.activities, student?.history?.activities]
    for (const list of lists) {
        const activity = (list ?? []).find((item) => item?.id != null && String(item.id) === String(activityId))
        const source = sourceOfActivity(activity)
        if (source != null) return source
    }
    return null
}

const SOURCE_TITLE_MAX = 40

/** Le titre de la feuille : « Générer à partir de l'exo « … » », le titre coupé à 40 (`truncateSourceTitle`). */
export function fromActivityTitle(title: string | null | undefined): string {
    if (!title) return "Autre exercice"
    const short = title.length > SOURCE_TITLE_MAX ? title.slice(0, SOURCE_TITLE_MAX - 1).trimEnd() + "…" : title
    return `Générer à partir de l'exo « ${short} »`
}

// ── Les champs du formulaire ────────────────────────────────────────────────

/**
 * Le formulaire du tuteur : tout, Classe et Matière compris. `GenerateActivityModal` les retirait du
 * formulaire (`hideClassSubject={!imageMode}`) pour les remonter dans son en-tête « Classe · Matière »,
 * visible hors mode programme : c'est là que le tuteur choisit la classe d'une activité commune.
 */
export const TUTOR_GENERATE_FORM_FIELDS: UIActivityGenerationField[] = [
    "scope",
    "chapter",
    "skillParts",
    "level",
    "duration",
    "instructions",
]

/** En mode programme : le chapitre du programme EST le cadrage, l'en-tête « Classe · Matière » disparaît. */
export const TUTOR_GENERATE_PROGRAM_FIELDS: UIActivityGenerationField[] = TUTOR_GENERATE_FORM_FIELDS.filter(
    (field) => field !== "scope"
)

/** `ParcoursChoiceModal` : Type, Durée, Instructions (optionnel) — le cadrage vient de l'exo. */
export const TUTOR_GENERATE_FROM_ACTIVITY_FIELDS: UIActivityGenerationField[] = ["level", "duration", "instructions"]

/** Le chapitre de l'exo n'est pas sûr : il se choisit sur place, en tête (`useSourceChapterFix`). */
export const TUTOR_GENERATE_FROM_ACTIVITY_FIX_FIELDS: UIActivityGenerationField[] = [
    "chapter",
    ...TUTOR_GENERATE_FROM_ACTIVITY_FIELDS,
]
